import {
  Drawer,
  Descriptions,
  Skeleton,
  Typography,
  Divider,
  Tag,
  Table,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useIntl } from "react-intl";
import { shortenString } from "@lib/helpers/StringHelper";
import styled from "styled-components";
import dayjs from "dayjs";
import {
  FRAUD_STATUS_COLOR,
  FraudDetectionMonetaryTransaction,
  MarketPlaceFraudDetection,
} from "@lib/types/FraudDetection";
import { DATE_FORMATS } from "@lib/constants/date";
import { AppJson } from "@lib/components/AppComponents";
import { useQuery } from "@tanstack/react-query";
import { apiGetMarketPlaceFraudDetectionById } from "@lib/api/marketPlace";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";

const StyledDescriptions = styled(Descriptions)`
  th,
  td {
    padding: 5px !important;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-descriptions-item-label {
    width: 200px;
  }
`;

const DEPOSIT_STATUS_COLOR: Record<string, string> = {
  Completed: "success",
  Pending: "warning",
  Failed: "error",
};

const SkeletonContent = () => (
  <StyledDescriptions bordered column={1}>
    {Array.from({ length: 9 }).map((_, index) => (
      <Descriptions.Item
        key={index}
        label={<Skeleton.Input active size="small" style={{ width: 100 }} />}
      >
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </Descriptions.Item>
    ))}
  </StyledDescriptions>
);

const MonetaryTransactionsSection = ({
  transactions,
  formatMessage,
}: {
  transactions: FraudDetectionMonetaryTransaction[];
  formatMessage: ReturnType<typeof useIntl>["formatMessage"];
}) => {
  if (!transactions?.length) return null;

  const columns: ColumnsType<FraudDetectionMonetaryTransaction> = [
    {
      title: formatMessage({ id: "common.depositId" }),
      dataIndex: "depositId",
      key: "depositId",
      width: 180,
      render: (depositId: string) => (
        <TableIdButton
          text={`#${shortenString(depositId)}`}
          textToBeCopied={depositId}
        />
      ),
    },
    {
      title: formatMessage({ id: "common.amount" }),
      dataIndex: "depositAmount",
      key: "depositAmount",
      width: 120,
      render: (amount: string) => (
        <Typography.Text strong>{amount || "-"}</Typography.Text>
      ),
    },
    {
      title: formatMessage({ id: "common.status" }),
      dataIndex: "depositStatus",
      key: "depositStatus",
      width: 130,
      render: (status: string) =>
        status ? (
          <Tag color={DEPOSIT_STATUS_COLOR[status] ?? "default"}>{status}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: formatMessage({ id: "common.paymentMethod" }),
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (paymentMethod: string | null) => paymentMethod || "-",
    },
  ];

  return (
    <>
      <Divider />
      <Typography.Title level={5} style={{ marginBottom: 12 }}>
        {formatMessage({ id: "common.monetaryTransactions" })}
      </Typography.Title>
      <Table
        size="small"
        bordered
        pagination={false}
        rowKey="depositId"
        dataSource={transactions}
        columns={columns}
        scroll={{ x: true }}
      />
    </>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
  item: MarketPlaceFraudDetection | null;
};

export const MarketPlaceFraudDetectionDrawer = (props: Props) => {
  const { open, onClose, item } = props;
  const { formatMessage } = useIntl();

  const fraudDetectionQuery = useQuery({
    queryFn: () => apiGetMarketPlaceFraudDetectionById(item?.id || ""),
    queryKey: ["market-place-fraud-detection", item?.id],
    enabled: !!item?.id && open,
  });

  const fraudData = fraudDetectionQuery.data?.data || item;
  const isLoading =
    fraudDetectionQuery.isLoading || fraudDetectionQuery.isFetching;
  const user = fraudData?.requestData?.user;

  return (
    <StyledDrawer
      title={formatMessage(
        { id: "common.previewTitle" },
        { title: formatMessage({ id: "sidebar.marketPlace.fraudDetection" }) },
      )}
      onClose={onClose}
      open={open}
      width={760}
      id="market-place-fraud-detection-drawer"
    >
      {!item || isLoading ? (
        <SkeletonContent />
      ) : (
        <>
          <StyledDescriptions bordered column={1}>
            <Descriptions.Item label="ID">
              <Typography.Text copyable={{ text: fraudData?.id }}>
                {shortenString(fraudData?.id || "", 40)}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item label={formatMessage({ id: "common.status" })}>
              {fraudData?.status ? (
                <Tag color={FRAUD_STATUS_COLOR[fraudData.status] ?? "default"}>
                  {fraudData.status}
                </Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>

            <Descriptions.Item label={formatMessage({ id: "common.score" })}>
              {(fraudData?.score ?? fraudData?.score === 0)
                ? fraudData.score
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item label={formatMessage({ id: "common.decision" })}>
              {fraudData?.decision || "-"}
            </Descriptions.Item>

            <Descriptions.Item label={formatMessage({ id: "common.summary" })}>
              {fraudData?.summary || "-"}
            </Descriptions.Item>

            <Descriptions.Item label={formatMessage({ id: "common.error" })}>
              {fraudData?.error || "-"}
            </Descriptions.Item>

            <Descriptions.Item label={formatMessage({ id: "common.userId" })}>
              <Typography.Text copyable={{ text: fraudData?.userId }}>
                {shortenString(fraudData?.userId || "", 40)}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item
              label={formatMessage({ id: "common.customerId" })}
            >
              <Typography.Text copyable={{ text: fraudData?.customerId }}>
                {shortenString(fraudData?.customerId || "", 40)}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item
              label={formatMessage({ id: "common.createdAt" })}
            >
              {fraudData?.createdAt
                ? dayjs(fraudData.createdAt).format(
                    DATE_FORMATS.DAY_MONTH_YEAR_TIME,
                  )
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={formatMessage({ id: "common.updatedAt" })}
            >
              {fraudData?.updatedAt
                ? dayjs(fraudData.updatedAt).format(
                    DATE_FORMATS.DAY_MONTH_YEAR_TIME,
                  )
                : "-"}
            </Descriptions.Item>
          </StyledDescriptions>

          {user && (
            <>
              <Divider />
              <Typography.Title level={5} style={{ marginBottom: 12 }}>
                {formatMessage({ id: "common.userInfo" })}
              </Typography.Title>
              <StyledDescriptions bordered column={1}>
                <Descriptions.Item label={formatMessage({ id: "common.name" })}>
                  {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
                    "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={formatMessage({ id: "common.email" })}
                >
                  {user.email || "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={formatMessage({ id: "common.phone" })}
                >
                  {user.phone || "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={formatMessage({ id: "common.country" })}
                >
                  {user.country || "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={formatMessage({ id: "common.language" })}
                >
                  {user.language || "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={formatMessage({ id: "common.leadSource" })}
                >
                  {user.leadSource || "-"}
                </Descriptions.Item>
              </StyledDescriptions>

              <MonetaryTransactionsSection
                transactions={user.monetaryTransactions}
                formatMessage={formatMessage}
              />
            </>
          )}

          {fraudData?.responseData && (
            <>
              <Divider />
              <Typography.Title level={5} style={{ marginBottom: 12 }}>
                {formatMessage({ id: "common.responseData" })}
              </Typography.Title>
              <AppJson src={fraudData.responseData} />
            </>
          )}

          {fraudData?.riskBreakdown && (
            <>
              <Divider />
              <Typography.Title level={5} style={{ marginBottom: 12 }}>
                {formatMessage({ id: "common.riskBreakdown" })}
              </Typography.Title>
              <AppJson src={fraudData.riskBreakdown} />
            </>
          )}
        </>
      )}
    </StyledDrawer>
  );
};
